import { useInfiniteQuery, useQuery } from '@tanstack/vue-query';
import { request } from '@/api/request';
import { useStore } from '@/store/useStore';
import { mergeQueryOptions, type QueryOptions } from '@/modules/query/utils';
import { ASSOCIATED_DISCUSSION_QUERY, DISCUSSIONS_INBOX_QUERY } from './queryKeys';

export type CommentView = {
  id: string;
  viewedAt: string;
};

type CommentAttachment = {
  commentID: string;
  createdAt: string;
  createdBy: string;
  uploadID: string;
};

export type Comment = {
  id: string;
  discussionID: string;
  message: string;
  createdAt: string;
  createdBy: string;
  orgID: string;
  attachments: CommentAttachment[] | null;
};

type UserData = {
  firstName: string;
  lastName: string;
  orgID: string;
};

type OrgData = {
  name: string;
  logo: string;
};

export type Discussion = {
  id: string;
  associatedID: string;
  associatedType: string;
  clientID: string;
  supplierID: string;
  createdAt: string;
  createdBy: string;
  archivedAt: string | null;
  comments: Comment[];
  latestCommentViewed: CommentView | null;
};

export type InboxDiscussion = {
  id: string;
  associatedID: string;
  associatedType: string;
  clientID: string;
  supplierID: string;
  createdAt: string;
  createdBy: string;
  archivedAt: string | null;
  lastMessage: Comment;
  latestCommentViewed: CommentView | null;
};

type DiscussionPreviewsVariables = {
  role: string;
  participationType: string;
  archived: boolean;
  orgFilter: string[];
  order?: string;
  page?: number;
  limit?: number;
};

export type DiscussionPreviewResponse = {
  discussions: InboxDiscussion[] | null;
  users: Record<string, UserData>;
  orgs: Record<string, OrgData>;
};

// TODO: we will hopefully refactor endpoints and
// get rid of the /inbox postfix in the urls in the future
export const getDiscussionPreviews = (payload: DiscussionPreviewsVariables) =>
  request<DiscussionPreviewResponse>('POST', '/discussions/inbox', payload);

export type DiscussionResponse = {
  discussion: Discussion;
  users: Record<string, UserData>;
  orgs: Record<string, OrgData>;
};

export const getDiscussion = (id: string) =>
  request<DiscussionResponse>('GET', `/discussions/${id}/inbox`);

export const startDiscussion = (
  associatedID: string,
  associatedType: any,
  message: any,
  clientID: string,
  supplierID: string,
  attachments: any,
) =>
  request('POST', `/discussion`, {
    associatedID,
    associatedType,
    message,
    clientID,
    supplierID,
    attachments,
  });

export const replyDiscussion = (discussionID: string, message: any, attachments: any) =>
  request('POST', `/discussion/${discussionID}`, {
    message,
    attachments,
  });

type AssociatedDiscussionsResponse = {
  discussions: Discussion[];
  users: Record<string, UserData>;
  orgs: Record<string, OrgData>;
};

export const getDiscussionsByAssociatedID = (associatedID: string) =>
  request<AssociatedDiscussionsResponse>('GET', `/discussions/associated/${associatedID}`);

export const archiveDiscussion = (discussionID: string) =>
  request('POST', `/discussion/${discussionID}/archive`);

export const unarchiveDiscussion = (discussionID: string) =>
  request('POST', `/discussion/${discussionID}/unarchive`);

export const markCommentsAsSeen = (discussionId: string, commentIds: string[]) =>
  request('POST', `/discussions/${discussionId}/mark-comments-as-seen`, { commentIds });

export function useAssociatedDiscussions(
  associatedID: MaybeRefOrGetter<string>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [ASSOCIATED_DISCUSSION_QUERY, toRef(associatedID)] as const,
      async queryFn({ queryKey }) {
        const res = await getDiscussionsByAssociatedID(queryKey[1]);
        return res.data;
      },
    }),
  );

  const discussions = computed(() => query.data.value?.discussions ?? []);
  const users = computed(() => query.data.value?.users ?? {});
  const orgs = computed(() => query.data.value?.orgs ?? {});

  const store = useStore();
  watchEffect(() => {
    if (!store || !query.data.value) return;
    store.commit('discussions/SET_DISCUSSIONS', query.data.value);
    store.commit('discussions/SET_USERS', query.data.value);
    store.commit('discussions/SET_ORGS', query.data.value);
  });

  return { ...query, query, discussions, users, orgs };
}

/**
 * Lightweight "has discussions" check for list/icon views.
 * Uses the same query key as useAssociatedDiscussions (cache shared) but never
 * syncs to Vuex, avoiding recursive updates when many instances render.
 * TODO: Find a better solution that also reduces the number of queries that are made
 */
export function useHasAssociatedDiscussions(
  associatedID: MaybeRefOrGetter<string>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [ASSOCIATED_DISCUSSION_QUERY, toRef(associatedID)] as const,
      async queryFn({ queryKey }) {
        const res = await getDiscussionsByAssociatedID(queryKey[1]);
        return res.data;
      },
    }),
  );
  const hasDiscussions = computed(() => (query.data.value?.discussions?.length ?? 0) > 0);
  return { ...query, hasDiscussions };
}

export function useDiscussionPreviews(
  payload: MaybeRefOrGetter<DiscussionPreviewsVariables>,
  options?: QueryOptions,
) {
  const limit = computed(() => toValue(payload).limit || 15);

  const query = useInfiniteQuery({
    queryKey: [DISCUSSIONS_INBOX_QUERY, toRef(payload), limit] as const,
    async queryFn({ queryKey, pageParam }) {
      const res = await getDiscussionPreviews({
        ...queryKey[1],
        page: pageParam,
        limit: queryKey[2],
      });
      return res.data;
    },
    initialPageParam: 0,
    getNextPageParam(lastPage, _allPages, lastPageParam) {
      // TODO: add page metadata to response, this isn't a safe way of doing this
      return lastPage?.discussions?.length === limit.value ? lastPageParam + 1 : null;
    },
    // TODO: add proper option merging (30 minutes)
    enabled: () => toValue(toValue(options)?.enabled) !== false,
  });

  const discussionPreviews = computed(
    () => query.data.value?.pages.map((page) => page.discussions ?? []).flat() ?? [],
  );

  const discussionPreviewsByAssociatedID = computed(() =>
    Object.groupBy(discussionPreviews.value, (item) => item.associatedID),
  );

  const usersByID = computed(() =>
    query.data.value
      ? Object.fromEntries(query.data.value.pages.map((page) => Object.entries(page.users)).flat())
      : {},
  );
  const orgsByID = computed(() =>
    query.data.value
      ? Object.fromEntries(query.data.value.pages.map((page) => Object.entries(page.orgs)).flat())
      : {},
  );

  return { ...query, discussionPreviews, discussionPreviewsByAssociatedID, usersByID, orgsByID };
}
